import type { RouteLocationNormalized } from 'vue-router'
import { router } from '@/router'
import { useRouteStore } from '@/store'

interface TabState {
  pinTabs: RouteLocationNormalized[]
  tabs: RouteLocationNormalized[]
  currentTabPath: string
}
export const useTabStore = defineStore('tab-store', {
  state: (): TabState => {
    return {
      pinTabs: [],
      tabs: [],
      currentTabPath: '',
    }
  },
  getters: {
    allTabs: state => [...state.pinTabs, ...state.tabs],
  },
  actions: {
    addTab(route: RouteLocationNormalized) {
      // 根据meta确定是否不添加，可用于错误页,登录页等
      if (route.meta.withoutTab)
        return

      // 如果标签名称已存在则不添加
      // if (this.hasExistTab(route.fullPath as string))
      //   return
      if (this.hasExistTab(route))
        return

      // 根据meta.pinTab传递到不同的分组中
      if (route.meta.pinTab) {
        const isHave = this.pinTabs.some(item => item.name === route.name)
        if (isHave)
          return
        this.pinTabs.push(route)
      }
      else {
        this.tabs.push(route)
        if (route.meta.addCacheRoutes) {
          const routeStore = useRouteStore()
          if (!routeStore.cacheRoutes.includes(route.name as string)) {
            routeStore.cacheRoutes.push(route.name as string)
          }
        }
      }
    },
    // 用于清除 ddCacheRoutes为true 的缓存
    delCatch(fullPath: string) {
      const routeStore = useRouteStore()
      const findRouteData = routeStore.rowRoutes.find(val => val.path === fullPath)
      // 限制除了addCacheRoutes为true的时候才删除这个路由的缓存
      if (findRouteData && findRouteData.addCacheRoutes) {
        const cacheIndex = routeStore.cacheRoutes.findIndex(val => val === findRouteData.name)
        routeStore.cacheRoutes.splice(cacheIndex, 1)
      }
    },
    async closeTab(fullPath: string) {
      this.delCatch(fullPath)

      const tabsLength = this.tabs.length
      // 如果动态标签大于一个,才会标签跳转
      if (this.tabs.length > 1) {
        // 获取关闭的标签索引
        const index = this.getTabIndex(fullPath)
        const isLast = index + 1 === tabsLength
        // 如果是关闭的当前页面，路由跳转到原先标签的后一个标签
        if (this.currentTabPath === fullPath && !isLast) {
          // 跳转到后一个标签
          router.push(this.tabs[index + 1].fullPath)
        }
        else if (this.currentTabPath === fullPath && isLast) {
          // 已经是最后一个了，就跳转前一个
          router.push(this.tabs[index - 1].fullPath)
        }
      }
      // 删除标签
      this.tabs = this.tabs.filter((item) => {
        return item.fullPath !== fullPath
      })
      // 删除后如果清空了，就跳转到默认首页
      if (tabsLength - 1 === 0)
        router.push('/')
    },

    closeOtherTabs(fullPath: string) {
      const index = this.getTabIndex(fullPath)
      const filterTbas = this.tabs.filter((item, i) => i !== index)
      this.tabs = this.tabs.filter((item, i) => i === index)
      filterTbas.forEach(item => {
        if (!item.meta.addCacheRoutes)
          return
        this.delCatch(item.fullPath)
      })
    },
    closeLeftTabs(fullPath: string) {
      const index = this.getTabIndex(fullPath)
      const filterTbas = this.tabs.filter((item, i) => i < index)
      this.tabs = this.tabs.filter((item, i) => i >= index)
      filterTbas.forEach(item => {
        if (!item.meta.addCacheRoutes)
          return
        this.delCatch(item.fullPath)
      })
    },
    closeRightTabs(fullPath: string) {
      const index = this.getTabIndex(fullPath)
      const filterTbas = this.tabs.filter((item, i) => i > index)
      this.tabs = this.tabs.filter((item, i) => i <= index)
      filterTbas.forEach(item => {
        if (!item.meta.addCacheRoutes)
          return
        this.delCatch(item.fullPath)
      })
    },
    clearAllTabs() {
      this.tabs.length = 0
      this.pinTabs.length = 0
    },
    closeAllTabs() {
      const filterTbas = [...this.tabs]
      this.tabs.length = 0
      router.push('/')
      filterTbas.forEach(item => {
        if (!item.meta.addCacheRoutes)
          return
        this.delCatch(item.fullPath)
      })
    },

    // hasExistTab(fullPath: string) {
    //   const _tabs = [...this.tabs, ...this.pinTabs]
    //   return _tabs.some((item) => {
    //     return item.fullPath === fullPath
    //   })
    // },
    hasExistTab(route: any) {
      const _tabs = [...this.tabs, ...this.pinTabs]
      if (JSON.stringify(route.params) !== '{}') {
        const index = this.tabs.findIndex((item) => {
          if (JSON.stringify(item.params) !== '{}') {
            return item.fullPath.split('/').slice(0, -1).join('/') === route.fullPath.split('/').slice(0, -1).join('/')
          }
        })
        if (index > -1) {
          this.tabs[index] = route
        }
        return index > -1
      }
      else if (JSON.stringify(route.query) !== '{}') {
        const index = this.tabs.findIndex((item) => {
          if (JSON.stringify(item.query) !== '{}') {
            return item.fullPath.split('?').slice(0, -1).join('/') === route.fullPath.split('?').slice(0, -1).join('/')
          }
          else {
            return item.fullPath === route.fullPath.split('?').slice(0, -1).join('/')
          }
        })
        if (index > -1) {
          this.tabs[index] = route
        }
        return index > -1
      }
      else {
        const index = this.tabs.findIndex((item) => {
          return item.fullPath.split('?').join('/') === route.fullPath.split('?').slice(0, -1).join('/') || item.fullPath.split('?').slice(0, -1).join('/') === route.fullPath.split('?').join('/')
        })
        if (index > -1) {
          this.tabs[index] = route
          return true
        }
        else {
          return _tabs.some((item) => {
            return item.fullPath === route.fullPath
          })
        }
      }
    },
    /* 设置当前激活的标签 */
    setCurrentTab(fullPath: string) {
      this.currentTabPath = fullPath
    },
    getTabIndex(fullPath: string) {
      return this.tabs.findIndex((item) => {
        return item.fullPath === fullPath
      })
    },
    modifyTab(fullPath: string, modifyFn: (route: RouteLocationNormalized) => void) {
      const index = this.getTabIndex(fullPath)
      modifyFn(this.tabs[index])
    },
  },
  persist: {
    storage: sessionStorage,
  },
})
